import { useTheme } from "@mui/material";
import { createSxStyles } from "@common/createSxStyles";

export type CombinedFieldAddonSide = "start" | "end";

// One of the two inputs gets a fixed width and the other takes the rest of the row
export type CombinedFieldFixedWidth = { addon: number } | { main: number };

/**
 * The one outline the library's amount + currency field draws around its two inputs, for two fields placed
 * side by side in a Box. The library does not export it, and its own field cannot be reused: it never lets its
 * unit be cleared and gives it no placeholder. The addon is the grey select joined to the main input.
 */
export function useCombinedFieldStyles(isFilled: boolean, addonSide: CombinedFieldAddonSide, fixedWidth: CombinedFieldFixedWidth) {
    const theme = useTheme();
    const restingBorder = `1px solid ${isFilled ? theme.palette.primary.main : "inherit"}`;
    const hoverBorder = `1px solid ${theme.palette.grey.A400}`;
    const focusBorder = `2px solid ${theme.palette.primary.main}`;
    const divider = theme.palette.grey.A400;

    const addonIsAtEnd = addonSide === "end";
    const main = addonIsAtEnd ? "& > div:first-of-type" : "& > div:last-of-type";
    const addon = addonIsAtEnd ? "& > div:last-of-type" : "& > div:first-of-type";
    // The side of each input that touches the other one
    const mainJoinedSide = addonIsAtEnd ? "Right" : "Left";
    const addonJoinedSide = addonIsAtEnd ? "Left" : "Right";
    const mainWidth = "main" in fixedWidth ? { width: fixedWidth.main } : { flex: 1 };
    const addonWidth = "addon" in fixedWidth ? { width: fixedWidth.addon } : { flex: 1, minWidth: 0 };

    return createSxStyles({
        combinedField: {
            display: "flex",
            position: "relative",
            width: "100%",
            "& .MuiOutlinedInput-notchedOutline:hover": {
                borderColor: "inherit",
            },
            [main]: {
                ...mainWidth,
                "& .MuiOutlinedInput-root": {
                    [`borderTop${mainJoinedSide}Radius`]: 0,
                    [`borderBottom${mainJoinedSide}Radius`]: 0,
                },
                "& .MuiOutlinedInput-root fieldset": {
                    border: restingBorder,
                    [`border${mainJoinedSide}Width`]: 0,
                },
                "&:hover .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    [`border${mainJoinedSide}Width`]: 0,
                },
            },
            [addon]: {
                ...addonWidth,
                "& .MuiOutlinedInput-root": {
                    [`borderTop${addonJoinedSide}Radius`]: 0,
                    [`borderBottom${addonJoinedSide}Radius`]: 0,
                    backgroundColor: theme.palette.grey[50],
                    "& fieldset": {
                        border: restingBorder,
                        [`border${addonJoinedSide}Color`]: divider,
                    },
                },
                "& .MuiFormHelperText-root": {
                    display: "none",
                },
            },
            "&:hover, &:hover:focus-within": {
                [`${main} .MuiOutlinedInput-root fieldset`]: {
                    border: hoverBorder,
                    [`border${mainJoinedSide}Width`]: 0,
                },
                [`${addon} .MuiOutlinedInput-root fieldset`]: {
                    border: hoverBorder,
                },
            },
            "&:focus-within": {
                [`${main} .MuiOutlinedInput-root fieldset`]: {
                    border: focusBorder,
                    [`border${mainJoinedSide}Width`]: 0,
                },
                [`${addon} .MuiOutlinedInput-root fieldset`]: {
                    border: focusBorder,
                    [`border${addonJoinedSide}Width`]: 1,
                    [`border${addonJoinedSide}Color`]: divider,
                },
            },
        },
    });
}
